import {Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {UUID} from '../../../shared/types/uuid.type';
import {CreateTranscodingPresetDto, TranscodingPreset} from './transcoding-preset.model';
import {ApiV2Service} from '../api-v2';

/**
 * Service for managing transcoding presets via REST API
 *
 * Provides high-level business logic for transcoding presets:
 * - CRUD operations with validation
 * - Data transformation and mapping
 * - Business rules enforcement
 * - Type-safe operations
 *
 * This service uses the universal ApiV2Service for HTTP communication.
 *
 * @example
 * ```TypeScript
 * constructor(private transcodingPresetsService: TranscodingPresetsService) {}
 *
 * // Get all presets
 * this.transcodingPresetsService.getAll({ page: 1, pageSize: 20 })
 *   .subscribe(response => console.log(response.data));
 *
 * // Create a new preset
 * this.transcodingPresetsService.create(newPreset)
 *   .subscribe(preset => console.log('Created:', preset));
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class TranscodingPresetsService {
  private readonly RESOURCE_BASE = '/transcoding-presets';

  constructor(
    private readonly api: ApiV2Service
  ) {
  }

  /**
   * Retrieves all transcoding presets
   *
   * @returns Observable of transcoding presets
   */
  public getAll(): Observable<TranscodingPreset[]> {
    return this.api.get<TranscodingPreset[]>(this.RESOURCE_BASE);
  }

  /**
   * Retrieves a single transcoding preset by ID
   *
   * @param id - UUID of the transcoding preset
   * @returns Observable of the transcoding preset
   */
  public getById(id: UUID): Observable<TranscodingPreset> {
    return this.api.get<TranscodingPreset>(`${this.RESOURCE_BASE}/${id}`);
  }

  /**
   * Creates a new transcoding preset
   *
   * @param dto - Data transfer object containing preset configuration
   * @returns Observable of the created transcoding preset with generated ID
   */
  public create(dto: CreateTranscodingPresetDto): Observable<TranscodingPreset> {
    return this.api.post<TranscodingPreset>(this.RESOURCE_BASE, dto);
  }

  /**
   * Deletes a transcoding preset by ID
   *
   * @param id - UUID of the transcoding preset to delete
   * @returns Observable that completes when deletion is successful
   */
  public delete(id: UUID): Observable<void> {
    return this.api.delete<void>(`${this.RESOURCE_BASE}/${id}`);
  }
}
